import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { parseISO, format } from 'date-fns';

// The server (SeoPageRenderer.cs) sends the same tags for blog posts.
// Anything the server sends must also be declared here, or React Helmet
// removes it when the page loads.
const SITE = "https://www.dysband.com";
const BAND = "Dirty Yachtin' Scoundrels";
const DEFAULT_IMAGE = `${SITE}/media/yacht_rock_band_dirty_yachtin_scoundrels_live.webp`;

// Strip HTML tags, decode entities, collapse whitespace, then trim to a length
const plainText = (html, max) => {
    const withoutTags = (html || '').replace(/<[^>]+>/g, ' ');
    const el = document.createElement('textarea');
    el.innerHTML = withoutTags; // decodes entities like &amp;
    const text = el.value.replace(/\s+/g, ' ').trim();
    return text.length <= max ? text : text.slice(0, max).trimEnd() + '...';
};

const absUrl = (u) =>
    !u ? DEFAULT_IMAGE : /^https?:\/\//i.test(u) ? u : SITE + (u.startsWith('/') ? '' : '/') + u;

const BlogPostDetail = () => {
    const { slug } = useParams();

    const [post, setPost] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        setLoading(true);
        setError(null);
        setNotFound(false);
        setPost(null);

        fetch('/api/blog')
            .then((res) => {
                if (!res.ok) throw new Error('Failed to load posts');
                return res.json();
            })
            .then((data) => {
                // Only published posts, matched case-insensitively (same as the server)
                const foundPost = data.find(
                    (p) =>
                        (p.slug || '').toLowerCase() === (slug || '').toLowerCase() &&
                        (p.visible === 1 || p.visible === true)
                );
                if (!foundPost) {
                    setNotFound(true);
                } else {
                    setPost(foundPost);
                }
                setLoading(false);
            })
            .catch((err) => {
                setError(err.message);
                setLoading(false);
            });
    }, [slug]);

    if (loading) return <p>Loading post...</p>;

    if (notFound) {
        return (
            <div className="blog-post-detail">
                <Helmet>
                    <title>{`Page Not Found | ${BAND}`}</title>
                    <meta name="robots" content="noindex, nofollow" />
                </Helmet>
                <h1>Post not found</h1>
                <Link to="/BlogPage" className="blog-post-detail-back">
                    ← Back to Captains Log
                </Link>
            </div>
        );
    }

    // A failed request is not a missing page, so no noindex here
    if (error) return <p style={{ color: 'red' }}>Error: {error}</p>;

    // Date from SQL Server ("2025-05-08 00:00:00.000"). Prefer createdAt, fall back to date.
    const rawDate = post.createdAt || post.date;
    const parsedDate = rawDate ? parseISO(rawDate.replace(' ', 'T')) : null;
    const formattedDate = parsedDate ? format(parsedDate, 'MMMM d, yyyy') : 'Unknown date';

    // Handle imageUrl field (API probably returns camelCase)
    const imageUrl = post.imageUrl || post.ImageUrl || post.ImageURL || '';
    const content = post.content || '';

    // SEO values (same rules as the server)
    const canonicalUrl = `${SITE}/BlogPage/${post.slug}`;
    const pageTitle = `${post.title} | ${BAND}`;
    const description = plainText(content, 155);
    const image = absUrl(imageUrl);
    const isoDate = rawDate ? rawDate.slice(0, 10) : null; // "2025-05-08"
    const validDate = isoDate && parseInt(isoDate.slice(0, 4), 10) >= 2000 ? isoDate : null;

    const schema = {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        "headline": post.title,
        "description": description,
        "image": image,
        "mainEntityOfPage": canonicalUrl,
        "author": post.author
            ? { "@type": "Person", "name": post.author }
            : { "@type": "Organization", "name": BAND },
        "publisher": { "@type": "Organization", "name": BAND, "url": SITE },
        ...(validDate ? { "datePublished": validDate, "dateModified": validDate } : {})
    };

    return (
        <div className="blog-post-detail">
            <Helmet>
                <title>{pageTitle}</title>
                <meta name="description" content={description} />
                <link rel="canonical" href={canonicalUrl} />

                <meta property="og:url" content={canonicalUrl} />
                <meta property="og:title" content={pageTitle} />
                <meta property="og:description" content={description} />
                <meta property="og:type" content="article" />
                <meta property="og:site_name" content={BAND} />
                <meta property="og:image" content={image} />

                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={pageTitle} />
                <meta name="twitter:description" content={description} />
                <meta name="twitter:image" content={image} />

                <script type="application/ld+json">{JSON.stringify(schema)}</script>
            </Helmet>

            <h1>{post.title}</h1>
            <div className="author">Updated On {formattedDate} By {post.author}</div>
            {imageUrl && (
                <img
                    src={imageUrl}
                    alt={post.title || "Blog post image"}
                    className="blog-post-image"
                />
            )}
            <div dangerouslySetInnerHTML={{ __html: content }} />
            <Link to="/BlogPage" className="blog-post-detail-back">
                ← Back to Captains Log
            </Link>
        </div>
    );
};

export default BlogPostDetail;